import { useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { useGame } from '../state/store'
import { STORY } from '../config/story'
import { ownedLetterChars } from '../engine/formulas'
import { LETTERS_BY_ID } from '../config/letters'
import { RARITY, UI } from '../config/colors'

function MaskedSentence({
  text,
  owned,
  newlyRevealed,
}: {
  text: string
  owned: Set<string>
  newlyRevealed?: Set<string>
}) {
  return (
    <span>
      {text.split('').map((ch, i) => {
        if (ch === ' ') return <span key={i}> </span>
        if (owned.has(ch)) {
          const def = Object.values(LETTERS_BY_ID).find((l) => l.letter === ch)
          const color = def ? RARITY[def.rarity] ?? UI.storyFallback : UI.storyFallback
          const isNew = newlyRevealed?.has(ch)
          return (
            <motion.span
              key={i}
              initial={isNew ? { rotateX: 90, opacity: 0 } : false}
              animate={{ rotateX: 0, opacity: 1 }}
              transition={{ duration: 0.3, delay: i * 0.02 }}
              style={{ color, display: 'inline-block' }}
            >
              {ch}
            </motion.span>
          )
        }
        return (
          <span key={i} className="text-ink-muted">
            *
          </span>
        )
      })}
    </span>
  )
}

export function StoryPanel() {
  const unlockedSentences = useGame((s) => s.unlockedSentences)
  const letters = useGame((s) => s.letters)
  const owned = ownedLetterChars(letters)
  const prevUnlocked = useRef<Set<number>>(new Set())
  const containerRef = useRef<HTMLDivElement>(null)

  // Track which sentences were just unlocked for typewriter animation
  const newlyUnlocked = new Set<number>()
  for (const idx of unlockedSentences) {
    if (!prevUnlocked.current.has(idx)) {
      newlyUnlocked.add(idx)
    }
  }

  // Auto-scroll to latest unlocked sentence
  useEffect(() => {
    if (newlyUnlocked.size > 0 && containerRef.current) {
      const lastIdx = Math.max(...newlyUnlocked)
      const sentences = containerRef.current.children
      if (sentences[lastIdx]) {
        sentences[lastIdx].scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }
    // Update ref after render
    prevUnlocked.current = new Set(unlockedSentences)
  }, [unlockedSentences])

  if (STORY.length === 0) return null

  // Collect newly revealed letters across all new sentences
  const newlyRevealed = new Set<string>()
  for (const idx of newlyUnlocked) {
    const text = STORY[idx]?.text ?? ''
    for (const ch of text) {
      if (owned.has(ch)) newlyRevealed.add(ch)
    }
  }

  return (
    <div ref={containerRef} className="rounded-lg border border-ink-border bg-ink-panel p-4">
      <div className="mb-3 font-bold text-ink-teal">CHRONICLE</div>
      <div className="space-y-2 text-sm leading-relaxed">
        {STORY.map((sentence, i) => {
          const unlocked = unlockedSentences.includes(i)
          const isNew = newlyUnlocked.has(i)
          return (
            <motion.div
              key={i}
              initial={isNew ? { opacity: 0, x: -10 } : false}
              animate={{ opacity: unlocked ? 1 : 0.5, x: 0 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className={unlocked ? 'text-ink-text' : 'text-ink-muted/50'}
            >
              {unlocked ? (
                <MaskedSentence text={sentence.text} owned={owned} newlyRevealed={isNew ? newlyRevealed : undefined} />
              ) : (
                <MaskedSentence text={sentence.text} owned={owned} />
              )}
            </motion.div>
          )
        })}
      </div>
    </div>
  )
}
